/**
 * ActivityHeatmap — (day-of-week × hour-of-day) request-density grid.
 *
 * Replaces the /distributions tab's two 1D bar charts with one 2D grid:
 * marginals (per-hour, per-dow totals) are recoverable by summing rows /
 * columns, but the joint count ("Monday 9am") can't be recovered from the
 * marginals. So the heatmap is strictly more informative than the pair of
 * distributions ever was.
 *
 * Data shape (from /salescoach/api/engagement/activity-heatmap):
 *   { dow: 0..6 (0=Mon), hour: 0..23, requests: number }[]
 *
 * Always 168 cells, zero-filled on the server — the component iterates a
 * fixed 7×24 matrix without guarding for undefined gaps. Dow/hour are CET
 * (Europe/Berlin) on the server so the grid reads on the wall clock
 * operators live on.
 *
 * Cell colour: alpha-interpolates `hsl(var(--primary))` against the grid's
 * max cell value. Zero-request cells render as cold `--muted` so dead slots
 * read as absent. Tooltip shows "{Weekday} {HH}:00 — N requests".
 *
 * Same horizontal-scroll pattern CohortHeatmap uses: the grid is nominally
 * 24 columns wide, which is too much for narrow viewports, so it gets its
 * own scroll container with a right-edge fade overlay.
 */

import * as React from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { ActivityHeatmapCell } from '@/services/api-types';

const DOW_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DOW_FULL = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

export interface ActivityHeatmapProps {
  cells: ActivityHeatmapCell[];
  isLoading?: boolean;
  /**
   * Day-of-week filter from the global FilterBar. Drives which rows render
   * (the backend already zeros the excluded buckets via weekendExclusionSQL,
   * but the user wants the rows themselves hidden so the grid reads as the
   * window they asked for — not a 7-row grid with flat-zero stripes). Omit
   * or pass 'all' to show all 7 rows.
   */
  dayFilter?: 'all' | 'weekdays' | 'weekends';
  /**
   * Drill-down click handler. When provided, cells with `requests > 0` become
   * keyboard-focusable buttons that open a modal via the parent. dow is
   * 0..6 (0=Mon), hour is 0..23 — both CET wall-clock, matching the
   * `(isodow - 1)` + `extract(hour)` predicates in `computeActivityHeatmap`.
   * Omit to keep the heatmap read-only (preserves existing callers).
   */
  onCellClick?: (dow: number, hour: number) => void;
}

// dow indexing: 0=Mon..6=Sun. Filter → visible indices.
function visibleDows(filter: 'all' | 'weekdays' | 'weekends'): number[] {
  if (filter === 'weekdays') return [0, 1, 2, 3, 4];
  if (filter === 'weekends') return [5, 6];
  return [0, 1, 2, 3, 4, 5, 6];
}

/**
 * Non-linear ramp — request distributions are long-tailed (a couple of peak
 * hours dwarf the quiet ones). A pure-linear `count/max` makes the quiet
 * band read as flat zero. We spend 70% of the saturation budget on the
 * bottom 30% of counts so low-traffic cells still differentiate.
 */
function densitySaturation(ratio: number): number {
  const r = Math.max(0, Math.min(1, ratio));
  const PIVOT = 0.3;
  const PIVOT_SAT = 0.7;
  if (r <= PIVOT) return (r / PIVOT) * PIVOT_SAT;
  return PIVOT_SAT + ((r - PIVOT) / (1 - PIVOT)) * (1 - PIVOT_SAT);
}

function cellStyle(
  requests: number,
  max: number,
): { background: string; color: string } {
  if (requests === 0 || max === 0) {
    return {
      background: 'hsl(var(--muted))',
      color: 'hsl(var(--muted-foreground))',
    };
  }
  const sat = densitySaturation(requests / max);
  const color =
    sat > 0.5 ? 'hsl(var(--primary-foreground))' : 'hsl(var(--foreground))';
  return {
    background: `hsl(var(--primary) / ${sat.toFixed(3)})`,
    color,
  };
}

function HeatmapLegend({ max }: { max: number }): React.ReactElement {
  const stops = [0, 0.1, 0.25, 0.5, 0.75, 1.0];
  return (
    <div
      data-testid="activity-heatmap-legend"
      className="flex items-center gap-2 pt-3 text-[11px] text-muted-foreground flex-wrap"
    >
      <span>Requests</span>
      <div className="flex gap-px">
        {stops.map((ratio) => {
          const count = Math.round(max * ratio);
          const { background, color } = cellStyle(count, max);
          return (
            <div
              key={ratio}
              className="h-4 w-10 flex items-center justify-center rounded-sm border border-border/60 tabular-nums"
              style={{ background, color, fontSize: 10 }}
            >
              {count}
            </div>
          );
        })}
      </div>
      <span>low &rarr; high</span>
    </div>
  );
}

export function ActivityHeatmap({
  cells,
  isLoading = false,
  dayFilter = 'all',
  onCellClick,
}: ActivityHeatmapProps): React.ReactElement {
  const dows = React.useMemo(() => visibleDows(dayFilter), [dayFilter]);
  // Mobile overflow guard (same pattern as CohortHeatmap). Hooks must run
  // on every render, BEFORE any early return — React error #310 otherwise.
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const [hasLeftScroll, setHasLeftScroll] = React.useState(false);
  const [hasRightScroll, setHasRightScroll] = React.useState(false);
  const recomputeOverflow = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    // 2026-04-24 audit: bidirectional fade. Once the user scrolls into the
    // afternoon hours they can lose sight of the left edge — the right-only
    // cue doesn't telegraph "scroll back". Track both edges with a 1px
    // tolerance for sub-pixel rounding.
    setHasLeftScroll(el.scrollLeft > 1);
    setHasRightScroll(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);
  React.useEffect(() => {
    const raf = requestAnimationFrame(recomputeOverflow);
    const onResize = () => recomputeOverflow();
    window.addEventListener('resize', onResize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
    };
  }, [recomputeOverflow, cells]);

  // Grid[dow][hour] lookup. Backend always sends 168 cells zero-filled, so
  // missing-cell handling is defensive only.
  const grid = React.useMemo(() => {
    const g: number[][] = Array.from({ length: 7 }, () =>
      Array.from({ length: 24 }, () => 0),
    );
    for (const c of cells) {
      if (c.dow < 0 || c.dow > 6 || c.hour < 0 || c.hour > 23) continue;
      g[c.dow][c.hour] = c.requests;
    }
    return g;
  }, [cells]);

  // Totals and `max` honour the visible rows only so the colour ramp is
  // anchored to what the user sees — hiding weekends shouldn't squash the
  // Mon-Fri ramp against a Sunday peak that's been filtered away.
  const { total, max } = React.useMemo(() => {
    let t = 0;
    let m = 0;
    for (const d of dows) {
      for (const v of grid[d]) {
        t += v;
        if (v > m) m = v;
      }
    }
    return { total: t, max: m };
  }, [grid, dows]);

  if (isLoading) {
    return (
      <div data-testid="activity-heatmap-skeleton" className="space-y-1">
        {dows.map((d) => (
          <div key={d} className="flex gap-1">
            {Array.from({ length: 24 }).map((_, c) => (
              <div
                key={c}
                className="h-9 w-9 sm:h-7 sm:w-7 rounded-sm bg-muted animate-pulse"
              />
            ))}
          </div>
        ))}
      </div>
    );
  }

  if (total === 0) {
    return (
      <div
        data-testid="activity-heatmap-empty"
        className="flex items-center justify-center py-12 text-sm text-muted-foreground"
      >
        No activity in selected period
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={100}>
      <div data-testid="activity-heatmap" className="relative">
        <p className="pb-2 text-[11px] text-muted-foreground">
          Hour of day (CET) × day of week — darker cells have more reactive
          requests. Scheduled briefs are excluded.
        </p>
        <div
          ref={scrollRef}
          onScroll={recomputeOverflow}
          className="overflow-x-auto [scrollbar-width:thin]"
        >
          <div className="inline-block min-w-full">
            {/* Hour header row — aligned with the grid columns. Widths
                follow the cell breakpoint (`w-9` mobile, `sm:w-7` desktop)
                so the "00 / 03 / 06 …" labels stay centred over their cells
                after the mobile-audit hitbox bump. */}
            <div className="flex gap-px pb-1 pl-[44px]">
              {Array.from({ length: 24 }).map((_, h) => (
                <div
                  key={h}
                  className="h-4 w-9 sm:w-7 flex items-center justify-center text-[9px] text-muted-foreground tabular-nums"
                >
                  {h % 3 === 0 ? String(h).padStart(2, '0') : ''}
                </div>
              ))}
            </div>
            {/* Visible rows × 24 cols. Row count follows the global
                dayFilter (weekdays → 5, weekends → 2, all → 7). */}
            {dows.map((d) => (
              <div key={d} className="flex items-center gap-px pb-px">
                <div className="sticky left-0 z-[1] bg-card w-[44px] pr-2 text-[11px] text-muted-foreground text-right tabular-nums">
                  {DOW_LABELS[d]}
                </div>
                {Array.from({ length: 24 }).map((_, h) => {
                  const v = grid[d][h];
                  const { background, color } = cellStyle(v, max);
                  const clickable = Boolean(onCellClick) && v > 0;
                  const commonProps = {
                    'data-testid': 'activity-heatmap-cell',
                    'data-dow': d,
                    'data-hour': h,
                    'data-requests': v,
                    // 2026-04-24 mobile audit: cells were `h-7 w-7` (28px)
                    // everywhere — below the touch-friendly ~36px floor.
                    // Bump to 36×36 on mobile while keeping 28×28 on sm+ so
                    // the desktop density doesn't regress. The hour-header
                    // row below mirrors this width at the same breakpoint.
                    className: `h-9 w-9 sm:h-7 sm:w-7 rounded-sm border border-border/50 flex items-center justify-center text-[10px] tabular-nums ${
                      clickable
                        ? 'cursor-pointer hover:ring-2 hover:ring-primary/60 hover:ring-offset-1 hover:ring-offset-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:ring-offset-card transition-shadow'
                        : 'cursor-default'
                    }`,
                    style: { background, color },
                  } as const;
                  return (
                    <Tooltip key={h}>
                      <TooltipTrigger asChild>
                        {clickable ? (
                          <button
                            type="button"
                            aria-label={`Drill into ${DOW_FULL[d]} ${String(h).padStart(2, '0')}:00 CET — ${v} request${v === 1 ? '' : 's'}`}
                            onClick={() => onCellClick?.(d, h)}
                            {...commonProps}
                          >
                            {v > 0 ? v : ''}
                          </button>
                        ) : (
                          <div {...commonProps}>{v > 0 ? v : ''}</div>
                        )}
                      </TooltipTrigger>
                      <TooltipContent className="max-w-[220px] text-left">
                        <div className="space-y-0.5 text-xs">
                          <div className="font-medium">
                            {DOW_FULL[d]} {String(h).padStart(2, '0')}:00 CET
                          </div>
                          <div className="text-muted-foreground">
                            {v} request{v === 1 ? '' : 's'}
                          </div>
                          {clickable && (
                            <div className="text-[10px] text-muted-foreground/70 pt-1">
                              Click to drilldown
                            </div>
                          )}
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
        {hasLeftScroll && (
          <div
            aria-hidden
            className="pointer-events-none absolute left-0 top-0 h-full w-8"
            style={{
              background:
                'linear-gradient(to right, hsl(var(--card)), transparent)',
            }}
          />
        )}
        {hasRightScroll && (
          <div
            aria-hidden
            className="pointer-events-none absolute right-0 top-0 h-full w-8"
            style={{
              background:
                'linear-gradient(to left, hsl(var(--card)), transparent)',
            }}
          />
        )}
        <HeatmapLegend max={max} />
      </div>
    </TooltipProvider>
  );
}
